// Guest passes (F-SES-06, F-SES-07; spec/05 §3.4, spec/06 §8.1, spec/07 §1, spec/12 §3.2, spec/13 §4; ADR-0059).
//
// A walk-in with no account buys play time at the counter as a **guest pass**: a pass line with `guest: true` on a
// counter sale that names no player (`CreateSaleLine`). Placing the sale mints the **guest** (`gst_…`) and its
// 6-character **ticket code**; once the sale is paid the guest holds the pass, and the code works at any PC the pass
// covers for `sessions.guestTicketValidityHours` (24 by default). A player can **claim** the code into their account for
// 7 days, moving its time left and its sessions.
//
//   GET   /v1/organizations/{o}/venues/{v}/guest-passes        the venue's guest passes: live ones, or one sale's   passes:read
//   POST  /v1/station/sign-in/ticket                            "I have a ticket": start a guest session on this PC  kiosk token
//   POST  /v1/me/guest-passes/claim                             claim a ticket into the signed-in player's account   signed in
//
// Void is the existing refund (`POST …/passes/holdings/{holdingId}/refund`) on the guest's holding: the money goes
// back to the tender and the time is removed, so the code has nothing left to start.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { GuestIdSchema, PassHoldingIdSchema, SaleIdSchema, SessionIdSchema, StationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { GuestTicketCodeSchema, TypedGuestTicketCodeSchema } from "../primitives/guest-ticket.js";
import { NonNegativeMoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { PASSES_READ } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { SessionSchema } from "./sessions.js";
/**
 * Where a guest pass is, derived from the guest, its sale and its holding:
 *
 *   awaiting_payment   the sale is placed and not yet paid: the code is shown but starts nothing yet
 *   active             paid, inside its ticket hours, with time left: the code starts play
 *   used_up            no time left (played through)
 *   expired            past its ticket hours, or its pass expired
 *   refunded           refunded (Void) or voided from Passes: the money went back, the time was removed
 *   claimed            claimed into a player's account: the code is spent, the time is the player's
 *   cancelled          the sale was cancelled before it was paid
 */
export const GuestPassStatusSchema = z
    .enum(['awaiting_payment', 'active', 'used_up', 'expired', 'refunded', 'claimed', 'cancelled'])
    .openapi('GuestPassStatus', { description: 'Where a guest pass is (spec/05 §3.4).' });
/** The guest's session running now, and the station it is on. */
export const GuestPassSessionSchema = z
    .object({ id: SessionIdSchema, stationId: StationIdSchema })
    .openapi('GuestPassSession', { description: 'A guest’s running session and its station.' });
/** A guest pass: the guest, its ticket code and the pass it holds (spec/05 §3.4). */
export const GuestPassSchema = z
    .object({
    id: GuestIdSchema,
    code: GuestTicketCodeSchema,
    status: GuestPassStatusSchema,
    /** The pass product's name at the version sold ("3 hours"). */
    passName: z.string(),
    /** The guest's holding once the sale is paid; null before. The Void action refunds it. */
    holdingId: PassHoldingIdSchema.nullable(),
    saleId: SaleIdSchema,
    /** What the guest paid for the pass line (its share of the sale's discounts taken off). */
    price: NonNegativeMoneySchema,
    /** The pass's seconds, and those left (from the ledger; 0 before the sale is paid). */
    grantedSeconds: z.int().min(0),
    remainingSeconds: z.int().min(0),
    /** Until when the code starts play: paid + `sessions.guestTicketValidityHours`. Null until paid. */
    ticketExpiresAt: IsoDateTimeSchema.nullable(),
    /** Until when a player can claim it: paid + 7 days. Null until paid. */
    claimableUntil: IsoDateTimeSchema.nullable(),
    /** When it was claimed into an account; null otherwise. */
    claimedAt: IsoDateTimeSchema.nullable(),
    /** The guest's session running now, and on which station; null when none. */
    session: GuestPassSessionSchema.nullable(),
    createdAt: IsoDateTimeSchema,
})
    .openapi('GuestPass', { description: 'A guest pass: a walk-in’s ticket code and the pass behind it (spec/05 §3.4).' });
export const GuestPassListSchema = z
    .object({
    /**
     * Whether this venue serves guests now (`sessions.guestsEnabled`, off by default): the counter offers guest passes
     * only then. Read here, beside the passes, so a cashier — who reads no settings — knows it.
     */
    guestsEnabled: z.boolean(),
    guestPasses: z.array(GuestPassSchema),
})
    .openapi('GuestPassList', { description: 'A venue’s guest passes, newest first.' });
export const GuestPassQuerySchema = z
    .object({
    /** Only this sale's guest passes, in any status: what the counter shows once the sale is paid. */
    saleId: SaleIdSchema.optional(),
    /** `live` (the default): the passes a guest can still play on or come back for — awaiting payment or active. `all`: every one sold in the last 7 days. */
    state: z.enum(['live', 'all']).optional(),
})
    .openapi('GuestPassQuery');
export const listGuestPassesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/guest-passes',
    operationId: 'listGuestPasses',
    tags: ['passes'],
    summary: 'The venue’s guest passes (F-SES-07)',
    description: 'The venue’s guest passes, newest first, each with its ticket code, the pass behind it, the time left (from the ' +
        'ledger), when the code stops working and the guest’s running session. `saleId` lists one sale’s, in any status: ' +
        'what the counter shows once the sale is paid. Without it, the live ones (awaiting payment or active); ' +
        '`state=all` adds the rest sold in the last 7 days. Requires `passes:read`.',
    [PERMISSION_EXTENSION]: PASSES_READ,
    request: { params: VenueScopeParamsSchema, query: GuestPassQuerySchema },
    responses: {
        200: jsonResponse(GuestPassListSchema, 'The guest passes.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// "I have a ticket" at the kiosk (F-SES-06, spec/12 §3.2)
// ---------------------------------------------------------------------------------------------
export const StationTicketRedeemSchema = z
    .object({ code: TypedGuestTicketCodeSchema })
    .openapi('StationTicketRedeem', { description: 'The ticket code the guest typed at the PC.' });
export const StationTicketStartedSchema = z
    .object({
    /** The guest's session, started on this station and funded only by the guest's pass. */
    session: SessionSchema,
})
    .openapi('StationTicketStarted', { description: 'The guest’s session, started on this PC.' });
export const redeemStationTicketRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/ticket',
    operationId: 'redeemStationTicket',
    tags: ['sessions', 'stations'],
    summary: 'Start a guest session on this PC with a ticket code (F-SES-06)',
    description: 'Station-authenticated: `Authorization: Bearer <kiosk token>` (spec/07 §4.1). The guest typed the ticket code from ' +
        'their receipt. The server checks it is this venue’s, paid, inside its ticket hours and with time left on a pass ' +
        'that covers this PC now, runs the start checks (spec/05 §3.2) and starts the session on this station funded by ' +
        'the guest’s pass alone — never pay as you go, a wallet or an account — then sends the station `session.start` ' +
        'with the signed lease, as a start from the console does. A code nobody here holds is `guest-ticket-invalid`, the ' +
        'same answer for every reason; five wrong codes on one PC in 15 minutes lock ticket entry there for 15 minutes ' +
        '(`guest-ticket-locked`). Every attempt also counts against the station’s sign-in limit. Audited.',
    request: {
        body: { content: { 'application/json': { schema: StationTicketRedeemSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(StationTicketStartedSchema, 'The guest’s session is running on this PC.'),
        400: problemResponse('The code is not one this venue holds.', 'validation-failed', 'guest-ticket-invalid'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        409: problemResponse('This venue serves no guests, the ticket has expired or has no time left, its pass does not cover this PC now, the PC is unavailable, or the ticket is in play on another PC.', 'guests-off', 'guest-ticket-expired', 'guest-ticket-used-up', 'guest-ticket-not-here', 'station-unavailable', 'already-in-session'),
        429: problemResponse('Too many wrong codes on this PC, or too many sign-in attempts.', 'guest-ticket-locked', 'rate-limited'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Claim a ticket into an account (spec/05 §3.4 item 5)
// ---------------------------------------------------------------------------------------------
export const ClaimGuestPassSchema = z
    .object({ code: TypedGuestTicketCodeSchema })
    .openapi('ClaimGuestPass', { description: 'The ticket code to claim into the signed-in player’s account.' });
/** The venue a claimed ticket was bought at. */
export const GuestPassVenueSchema = z
    .object({ id: VenueIdSchema, name: z.string() })
    .openapi('GuestPassVenue', { description: 'The venue a guest ticket was bought at.' });
export const GuestPassClaimedSchema = z
    .object({
    /** The venue the ticket was bought at. */
    venue: GuestPassVenueSchema,
    /** The pass's name. */
    passName: z.string(),
    /** The time moved into the account (its seconds left on the pass). */
    movedSeconds: z.int().min(0),
    /** When the moved pass stops being valid, by its own terms; null for one valid until first use. */
    passExpiresAt: IsoDateTimeSchema.nullable(),
    /** The guest's sessions now in the player's history. */
    sessions: z.int().min(0),
})
    .openapi('GuestPassClaimed', { description: 'A guest ticket, claimed: its time and sessions are now the player’s.' });
export const claimGuestPassRoute = createRoute({
    method: 'post',
    path: '/v1/me/guest-passes/claim',
    operationId: 'claimGuestPass',
    tags: ['me'],
    summary: 'Claim a guest ticket into your account',
    description: 'Needs a signed-in player: a session cookie in a browser, `Authorization: Bearer` in the apps. About the caller ' +
        'only. Within 7 days of buying it, a guest ticket’s pass — with whatever time it has left, under its own terms — ' +
        'and its sessions become the player’s, and the code stops working. Refused while the ticket is in play on a PC, ' +
        'once claimed, and after 7 days (`guest-claim-unavailable`); a code nobody holds is `guest-ticket-invalid`. ' +
        'Ten attempts an hour per player. Audited as the player.',
    request: {
        body: { content: { 'application/json': { schema: ClaimGuestPassSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(GuestPassClaimedSchema, 'The ticket’s pass and sessions are now the player’s.'),
        400: problemResponse('The code is not one anybody holds.', 'validation-failed', 'guest-ticket-invalid'),
        401: commonProblemResponses[401],
        404: problemResponse('The caller has no player profile.', 'not-found'),
        409: problemResponse('The ticket cannot be claimed now.', 'guest-claim-unavailable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const GUEST_PASS_ROUTES = [listGuestPassesRoute, redeemStationTicketRoute, claimGuestPassRoute];
//# sourceMappingURL=guest-passes.js.map